import React, { useState, useCallback } from 'react';
import { adminFetch } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';
import * as XLSX from 'xlsx';

// Ant Design imports
import {
  Card,
  Typography,
  Button,
  Alert,
  Space,
  Steps,
  Upload,
  Table,
  Tag,
  Progress,
  Row,
  Col,
  Statistic,
  Tooltip,
  Divider,
  message,
  Flex,
  Select,
  Result,
} from 'antd';
import type { TableColumnsType, UploadFile } from 'antd';

// Ant Design Icons
import {
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ImportOutlined,
  CarOutlined,
  AppstoreOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Dragger } = Upload;

// Types for import data
interface VehicleImportRow {
  make: string;
  model: string;
  variants?: string;
  fuel_types?: string;
  _rowIndex?: number;
  _status?: 'valid' | 'error' | 'duplicate';
  _error?: string;
}

interface SubAggregateImportRow {
  source: string;
  sub_aggregate: string;
  normalized_name?: string;
  _rowIndex?: number;
  _status?: 'valid' | 'error' | 'duplicate';
  _error?: string;
}

interface PreviewData {
  rows: (VehicleImportRow | SubAggregateImportRow)[];
  totalRows: number;
  validRows: number;
  duplicates: number;
  errors: number;
}

type ImportType = 'vehicle_database' | 'sub_aggregates';

function DataImportManagement() {
  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [importType, setImportType] = useState<ImportType | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    inserted: number;
    skipped: number;
    errors: number;
    errorDetails?: string[];
  } | null>(null);

  // Template generation
  const downloadTemplate = (type: ImportType) => {
    let data: any[];
    let filename: string;

    if (type === 'vehicle_database') {
      data = [
        {
          make: 'Maruti Suzuki',
          model: 'Swift',
          variants: 'VXi,ZXi,LXi',
          fuel_types: 'Petrol,Diesel',
        },
        {
          make: 'Hyundai',
          model: 'i20',
          variants: 'Sportz,Asta,Magna',
          fuel_types: 'Petrol,Diesel',
        },
        {
          make: 'Tata',
          model: 'Nexon',
          variants: 'XM,XZ,XZA Plus',
          fuel_types: 'Petrol,Diesel,EV',
        },
      ];
      filename = 'vehicle_database_template.xlsx';
    } else {
      data = [
        {
          source: 'tvs',
          sub_aggregate: 'Brake Pad',
          normalized_name: 'Brake Pads',
        },
        {
          source: 'boodmo',
          sub_aggregate: 'Braking System - Brake Pad',
          normalized_name: 'Brake Pads',
        },
        {
          source: 'tvs',
          sub_aggregate: 'Air Filter',
          normalized_name: 'Air Filters',
        },
      ];
      filename = 'sub_aggregates_template.xlsx';
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');

    // Add column widths
    ws['!cols'] = [
      { wch: 20 },
      { wch: 30 },
      { wch: 30 },
      { wch: 25 },
    ];

    XLSX.writeFile(wb, filename);
    message.success(`Template downloaded: ${filename}`);
  };

  // Parse uploaded file
  const parseFile = useCallback(async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  }, []);

  // Validate rows
  const validateRows = useCallback(
    (rows: any[], type: ImportType): PreviewData => {
      const seen = new Set<string>();
      let validRows = 0;
      let duplicates = 0;
      let errors = 0;

      const validatedRows = rows.map((row, index) => {
        const validatedRow = {
          ...row,
          _rowIndex: index + 1,
          _status: 'valid' as const,
          _error: undefined as string | undefined,
        };

        if (type === 'vehicle_database') {
          // Validate vehicle database row
          if (!row.make || !row.model) {
            validatedRow._status = 'error';
            validatedRow._error = 'Make and Model are required';
            errors++;
          } else {
            const key = `${row.make}|${row.model}`.toLowerCase();
            if (seen.has(key)) {
              validatedRow._status = 'duplicate';
              validatedRow._error = 'Duplicate entry (will be skipped)';
              duplicates++;
            } else {
              seen.add(key);
              validRows++;
            }
          }
        } else {
          // Validate sub_aggregates row
          if (!row.source || !row.sub_aggregate) {
            validatedRow._status = 'error';
            validatedRow._error = 'Source and Sub Aggregate are required';
            errors++;
          } else if (!['tvs', 'boodmo'].includes(row.source.toLowerCase())) {
            validatedRow._status = 'error';
            validatedRow._error = 'Source must be "tvs" or "boodmo"';
            errors++;
          } else {
            const key = `${row.source}|${row.sub_aggregate}`.toLowerCase();
            if (seen.has(key)) {
              validatedRow._status = 'duplicate';
              validatedRow._error = 'Duplicate entry (will be skipped)';
              duplicates++;
            } else {
              seen.add(key);
              validRows++;
            }
          }
        }

        return validatedRow;
      });

      return {
        rows: validatedRows,
        totalRows: rows.length,
        validRows,
        duplicates,
        errors,
      };
    },
    []
  );

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    try {
      const data = await parseFile(file);
      if (data.length === 0) {
        message.error('File is empty or has no valid data');
        return false;
      }

      const preview = validateRows(data, importType!);
      setPreviewData(preview);
      setCurrentStep(2);
      return false; // Prevent auto upload
    } catch (err: any) {
      message.error(`Failed to parse file: ${err.message}`);
      return false;
    }
  };

  // Perform import
  const performImport = async () => {
    if (!previewData || !importType) return;

    setImporting(true);
    setImportProgress(0);

    try {
      // Filter valid rows only
      const validRows = previewData.rows.filter((row) => row._status === 'valid');

      // Prepare payload
      const payload = {
        import_type: importType,
        data: validRows.map((row) => {
          // Remove internal fields
          const { _rowIndex, _status, _error, ...cleanRow } = row as any;
          return cleanRow;
        }),
      };

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await adminFetch(`${getBackendUrl()}/api/admin/import/${importType}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      clearInterval(progressInterval);
      setImportProgress(100);

      if (response.ok) {
        const result = await response.json();
        setImportResult({
          success: true,
          inserted: result.data?.inserted || validRows.length,
          skipped: result.data?.skipped || previewData.duplicates,
          errors: result.data?.errors || 0,
          errorDetails: result.data?.error_details,
        });
        setCurrentStep(3);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setImportResult({
          success: false,
          inserted: 0,
          skipped: 0,
          errors: previewData.validRows,
          errorDetails: [errorData.message || errorData.detail || 'Import failed'],
        });
        setCurrentStep(3);
      }
    } catch (err: any) {
      setImportResult({
        success: false,
        inserted: 0,
        skipped: 0,
        errors: previewData.validRows,
        errorDetails: [err.message || 'Import failed'],
      });
      setCurrentStep(3);
    } finally {
      setImporting(false);
    }
  };

  // Reset wizard
  const resetWizard = () => {
    setCurrentStep(0);
    setImportType(null);
    setFileList([]);
    setPreviewData(null);
    setImportProgress(0);
    setImportResult(null);
  };

  // Get status color for preview table
  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'valid':
        return 'success';
      case 'error':
        return 'error';
      case 'duplicate':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Preview table columns for Vehicle Database
  const vehicleColumns: TableColumnsType<VehicleImportRow> = [
    {
      title: 'Row',
      dataIndex: '_rowIndex',
      key: '_rowIndex',
      width: 60,
    },
    {
      title: (
        <Space>
          Make
          <Tooltip title="Vehicle manufacturer (e.g., Maruti Suzuki, Hyundai)">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'make',
      key: 'make',
    },
    {
      title: (
        <Space>
          Model
          <Tooltip title="Vehicle model name (e.g., Swift, i20)">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: (
        <Space>
          Variants
          <Tooltip title="Comma-separated list of variants (e.g., VXi, ZXi, LXi)">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'variants',
      key: 'variants',
      render: (value: string) => value || '-',
    },
    {
      title: (
        <Space>
          Fuel Types
          <Tooltip title="Comma-separated list of fuel types (e.g., Petrol, Diesel)">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'fuel_types',
      key: 'fuel_types',
      render: (value: string) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: '_status',
      key: '_status',
      width: 120,
      render: (status: string, record: VehicleImportRow) => (
        <Tooltip title={record._error}>
          <Tag
            color={getStatusColor(status)}
            icon={
              status === 'valid' ? (
                <CheckCircleOutlined />
              ) : status === 'error' ? (
                <CloseCircleOutlined />
              ) : (
                <WarningOutlined />
              )
            }
          >
            {status}
          </Tag>
        </Tooltip>
      ),
    },
  ];

  // Preview table columns for SubAggregates
  const subAggregateColumns: TableColumnsType<SubAggregateImportRow> = [
    {
      title: 'Row',
      dataIndex: '_rowIndex',
      key: '_rowIndex',
      width: 60,
    },
    {
      title: (
        <Space>
          Source
          <Tooltip title="Catalog source: 'tvs' or 'boodmo'">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (value: string) => (
        <Tag color={value?.toLowerCase() === 'tvs' ? 'blue' : 'green'}>{value}</Tag>
      ),
    },
    {
      title: (
        <Space>
          Sub Aggregate
          <Tooltip title="Original category name from the catalog">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'sub_aggregate',
      key: 'sub_aggregate',
    },
    {
      title: (
        <Space>
          Normalized Name
          <Tooltip title="Standardized name for mapping across catalogs (optional)">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'normalized_name',
      key: 'normalized_name',
      render: (value: string) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: '_status',
      key: '_status',
      width: 120,
      render: (status: string, record: SubAggregateImportRow) => (
        <Tooltip title={record._error}>
          <Tag
            color={getStatusColor(status)}
            icon={
              status === 'valid' ? (
                <CheckCircleOutlined />
              ) : status === 'error' ? (
                <CloseCircleOutlined />
              ) : (
                <WarningOutlined />
              )
            }
          >
            {status}
          </Tag>
        </Tooltip>
      ),
    },
  ];

  // Step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        // Step 1: Select Import Type
        return (
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Card
                hoverable
                onClick={() => {
                  setImportType('vehicle_database');
                  setCurrentStep(1);
                }}
                style={{
                  height: '100%',
                  borderColor: importType === 'vehicle_database' ? '#1677ff' : undefined,
                  cursor: 'pointer',
                }}
              >
                <Flex align="center" gap={16}>
                  <CarOutlined style={{ fontSize: 48, color: '#1677ff' }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Vehicle Database
                    </Title>
                    <Text type="secondary">
                      Import Make, Model, Variants, and Fuel Types for the vehicle reference database.
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadTemplate('vehicle_database');
                        }}
                        style={{ padding: 0 }}
                      >
                        Download Template
                      </Button>
                    </div>
                  </div>
                </Flex>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card
                hoverable
                onClick={() => {
                  setImportType('sub_aggregates');
                  setCurrentStep(1);
                }}
                style={{
                  height: '100%',
                  borderColor: importType === 'sub_aggregates' ? '#1677ff' : undefined,
                  cursor: 'pointer',
                }}
              >
                <Flex align="center" gap={16}>
                  <AppstoreOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      SubAggregate Mappings
                    </Title>
                    <Text type="secondary">
                      Import TVS/Boodmo category mappings for part classification normalization.
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadTemplate('sub_aggregates');
                        }}
                        style={{ padding: 0 }}
                      >
                        Download Template
                      </Button>
                    </div>
                  </div>
                </Flex>
              </Card>
            </Col>
          </Row>
        );

      case 1:
        // Step 2: Upload File
        return (
          <div>
            <Alert
              type="info"
              showIcon
              message={`Upload a CSV or Excel file for ${
                importType === 'vehicle_database' ? 'Vehicle Database' : 'SubAggregate'
              } import`}
              description={
                importType === 'vehicle_database' ? (
                  <div>
                    <Text>Required columns: <Text strong>make</Text>, <Text strong>model</Text></Text>
                    <br />
                    <Text>Optional columns: <Text strong>variants</Text> (comma-separated), <Text strong>fuel_types</Text> (comma-separated)</Text>
                  </div>
                ) : (
                  <div>
                    <Text>Required columns: <Text strong>source</Text> (tvs or boodmo), <Text strong>sub_aggregate</Text></Text>
                    <br />
                    <Text>Optional columns: <Text strong>normalized_name</Text></Text>
                  </div>
                )
              }
              style={{ marginBottom: 24 }}
            />

            <Dragger
              accept=".csv,.xlsx,.xls"
              fileList={fileList}
              beforeUpload={(file) => {
                setFileList([file]);
                return handleFileUpload(file);
              }}
              onRemove={() => {
                setFileList([]);
                setPreviewData(null);
              }}
              style={{ padding: 24 }}
            >
              <p className="ant-upload-drag-icon">
                <FileExcelOutlined style={{ fontSize: 48, color: '#52c41a' }} />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">Support for CSV and Excel files (.csv, .xlsx, .xls)</p>
            </Dragger>

            <Flex justify="space-between" style={{ marginTop: 24 }}>
              <Button onClick={() => setCurrentStep(0)}>Back</Button>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => downloadTemplate(importType!)}
              >
                Download Template
              </Button>
            </Flex>
          </div>
        );

      case 2:
        // Step 3: Preview & Validate
        return (
          <div>
            {/* Summary Stats */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic title="Total Rows" value={previewData?.totalRows || 0} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Valid"
                    value={previewData?.validRows || 0}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title={
                      <Space>
                        Duplicates
                        <Tooltip title="Duplicates will be skipped (INSERT IGNORE behavior)">
                          <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
                        </Tooltip>
                      </Space>
                    }
                    value={previewData?.duplicates || 0}
                    valueStyle={{ color: '#faad14' }}
                    prefix={<WarningOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Errors"
                    value={previewData?.errors || 0}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            {previewData?.errors && previewData.errors > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${previewData.errors} row(s) have validation errors and will not be imported`}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Preview Table */}
            <Card title="Data Preview" size="small">
              {importType === 'vehicle_database' ? (
                <Table
                  dataSource={previewData?.rows as VehicleImportRow[] || []}
                  columns={vehicleColumns}
                  rowKey="_rowIndex"
                  size="small"
                  scroll={{ y: 300 }}
                  pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total ${total} rows`,
                  }}
                />
              ) : (
                <Table
                  dataSource={previewData?.rows as SubAggregateImportRow[] || []}
                  columns={subAggregateColumns}
                  rowKey="_rowIndex"
                  size="small"
                  scroll={{ y: 300 }}
                  pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total ${total} rows`,
                  }}
                />
              )}
            </Card>

            <Flex justify="space-between" style={{ marginTop: 24 }}>
              <Button onClick={() => setCurrentStep(1)}>Back</Button>
              <Button
                type="primary"
                icon={<ImportOutlined />}
                onClick={performImport}
                disabled={!previewData?.validRows || previewData.validRows === 0}
                loading={importing}
              >
                {importing ? 'Importing...' : `Import ${previewData?.validRows || 0} Valid Rows`}
              </Button>
            </Flex>

            {importing && (
              <div style={{ marginTop: 24 }}>
                <Progress percent={importProgress} status="active" />
                <Text type="secondary">Processing import...</Text>
              </div>
            )}
          </div>
        );

      case 3:
        // Step 4: Import Results
        return (
          <Result
            status={importResult?.success ? 'success' : 'error'}
            title={importResult?.success ? 'Import Completed Successfully' : 'Import Failed'}
            subTitle={
              importResult?.success
                ? `Successfully imported ${importResult.inserted} records`
                : 'There was an error during the import process'
            }
            extra={[
              <Button key="new" type="primary" icon={<ReloadOutlined />} onClick={resetWizard}>
                Start New Import
              </Button>,
            ]}
          >
            <div style={{ textAlign: 'left', maxWidth: 400, margin: '0 auto' }}>
              <Divider />
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Inserted"
                    value={importResult?.inserted || 0}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Skipped"
                    value={importResult?.skipped || 0}
                    valueStyle={{ color: '#faad14' }}
                    prefix={<WarningOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Errors"
                    value={importResult?.errors || 0}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Col>
              </Row>

              {importResult?.errorDetails && importResult.errorDetails.length > 0 && (
                <>
                  <Divider />
                  <Alert
                    type="error"
                    message="Error Details"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {importResult.errorDetails.slice(0, 5).map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                        {importResult.errorDetails.length > 5 && (
                          <li>...and {importResult.errorDetails.length - 5} more errors</li>
                        )}
                      </ul>
                    }
                  />
                </>
              )}
            </div>
          </Result>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          Data Import
        </Title>
        {currentStep > 0 && currentStep < 3 && (
          <Button onClick={resetWizard}>Cancel Import</Button>
        )}
      </Flex>

      {/* Info Alert */}
      <Alert
        type="info"
        showIcon
        message="Bulk Data Import"
        description="Import vehicle database or sub-aggregate reference data from CSV/Excel files. Download a template to see the required format."
        style={{ marginBottom: 24 }}
      />

      {/* Steps */}
      <Steps
        current={currentStep}
        style={{ marginBottom: 32 }}
        items={[
          {
            title: 'Select Type',
            description: 'Choose import type',
            icon: <AppstoreOutlined />,
          },
          {
            title: 'Upload File',
            description: 'CSV or Excel',
            icon: <UploadOutlined />,
          },
          {
            title: 'Preview',
            description: 'Validate data',
            icon: <CheckCircleOutlined />,
          },
          {
            title: 'Complete',
            description: 'View results',
            icon: <ImportOutlined />,
          },
        ]}
      />

      {/* Step Content */}
      <Card>{renderStepContent()}</Card>
    </div>
  );
}

export default DataImportManagement;
